import React, { useState, useEffect } from 'react';
import { Calendar, Loader2, ArrowRight } from 'lucide-react';
import SectionTitle from './SectionTitle';
import Button from './Button';
import SEO from './SEO';
import config from '../utils/config';

const RachnayePage = () => {
  const [poems, setPoems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPoems = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.url}/rachnaye`);
        if (!response.ok) throw new Error('Failed to fetch poems');
        const data = await response.json();
        setPoems(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch (err) {
        setError('रचनाएँ लोड करने में समस्या आई है।');
      } finally {
        setLoading(false);
      }
    };
    fetchPoems();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hi-IN', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>सभी रचनाएँ</SectionTitle>
        <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
        <p className="text-lg text-gray-600">रचनाएँ लोड हो रही हैं...</p>
      </div>
    </section>
  );
  if (error) return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 text-center">
        <SectionTitle>सभी रचनाएँ</SectionTitle>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
          <p className="text-lg">{error}</p>
        </div>
      </div>
    </section>
  );

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50">
      <SEO 
        title="सभी रचनाएँ - सीधी सोच सीधी बात" 
        description="सीधी सोच सीधी बात की सभी कविताएँ और साहित्यिक रचनाएँ। राष्ट्रप्रेम, समाज सुधार और स्वदेशी भावना से भरी रचनाओं का संग्रह।" 
        keywords="रचनाएँ, कविताएँ, साहित्य, राष्ट्रप्रेम, समाज सुधार, स्वदेशी भावना, सीधी सोच सीधी बात"
        url="/rachnaye"
      />
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <SectionTitle>सभी रचनाएँ</SectionTitle>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">संग्रहित कविताएँ और साहित्यिक रचनाएँ</p>
        </div>
        {poems.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="text-6xl mb-4">📜</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">कोई रचना नहीं मिली</h3>
              <p className="text-gray-500">अभी तक कोई रचना नहीं है।</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {poems.map(poem => (
              <div key={poem.id} className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 group">
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">{poem.title}</h3>
                  <div className="flex items-center text-gray-600 text-sm mb-2">
                    <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                    <span>{formatDate(poem.date)}</span>
                  </div>
                  {poem.author && (
                    <div className="flex items-center text-gray-600 text-sm mb-2">
                      <span className="font-semibold">{poem.author}</span>
                    </div>
                  )}
                  {poem.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{poem.description}</p>
                  )}
                  <div className="mt-auto flex justify-end">
                    <Button to={`/rachnaye/${poem.id}`} size="sm" rightIcon={ArrowRight} variant="primary" className="mt-2">और पढ़ें</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default RachnayePage;